import { type ReactElement, useMemo } from 'react'
import { DataTable } from 'mantine-datatable'
import { WorkflowDeprecatedCheck, WorkflowDisabledTooltip } from 'app/components/workflow'
import { type AccountStatusData } from '@terros/common'
import { Link, useNavigate, createFileRoute } from '@tanstack/react-router'
import { Group, Switch } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { translateTitles, useAccountStatuses, useTranslation } from '@front/model'
import { ButtonAdd, IconDisplay, NotReady, Page } from '@components'

const COLUMNS_BASE = [
  {
    accessor: 'icon',
    title: 'Icon',
    render: (record: AccountStatusData) => {
      if (!record.icon) return
      return <IconDisplay color={record.color} icon={record.icon} />
    },
  },
  { accessor: 'name', title: 'Name' },
  { accessor: 'order', title: 'Order' },
  { accessor: 'short', title: 'Short' },
  { accessor: 'plural', title: 'Plural' },
  { accessor: 'description', title: 'Description' },
]

function AccountStatusesPage(): ReactElement {
  return (
    <WorkflowDeprecatedCheck>
      {({ isWorkflowForCanvassEnabled }) => (
        <AccountStatuses isWorkflowEnabled={isWorkflowForCanvassEnabled} />
      )}
    </WorkflowDeprecatedCheck>
  )
}

const AccountStatuses = ({ isWorkflowEnabled }: { isWorkflowEnabled: boolean }): ReactElement => {
  const { t } = useTranslation()
  const [showArchived, { toggle }] = useQueryDisclosure('showArchived')
  const { data, isLoading, error, refetch, isFetching } = useAccountStatuses({
    archived: showArchived ? 'all' : false,
  })
  const navigate = useNavigate()

  const columns = useMemo(() => translateTitles(COLUMNS_BASE, t), [t])

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Statuses' isLoading={isLoading} />

  const canEdit = !data.includesDefaults

  const handleRowClick = ({ record }: { record: AccountStatusData }): void => {
    if (canEdit) navigate({ to: record.statusId })
  }

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Account Stages')}>
        <Group>
          {canEdit && (
            <WorkflowDisabledTooltip isWorkflowEnabled={isWorkflowEnabled}>
              <ButtonAdd component={Link} to='add' />
            </WorkflowDisabledTooltip>
          )}
        </Group>
      </Page.Heading>
      <Page.FunctionBar justify='end'>
        <Switch checked={showArchived} label={t('Show Archived')} labelPosition='left' onChange={toggle} />
      </Page.FunctionBar>
      <Page.Content>
        <DataTable
          highlightOnHover
          striped
          idAccessor={(record) => record.statusId}
          records={data.statuses}
          columns={columns}
          onRowClick={handleRowClick}
        />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/settings/status/')({
  component: AccountStatusesPage,
})
